/*eslint-disable*/
import React, { useState, useEffect, useCallback, useReducer, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Loader, Button, BaseSelect, Picker, RadioButtonGroup, Checkbox } from 'ui-kit';
import { ReportEntity } from '_entities';
import { BUTTON_TYPES, EXPORT_DATA_OPTIONS, EXPORT_DATA_OPTIONS_NAMES, MESSAGES, URLS } from '_constants';
import BackPageButton from 'modules/BackPageButton';
import { notification } from 'utils/services';
import {
  formNames,
  formInitialState,
  FORM_ACTION_TYPES,
  TOBACCO_LANDSCAPE_OPTIONS,
  REPORT_GEO_TYPES,
  NEW_REPORTS_REQUIRED_FIELDS,
  DOWNLOAD_TYPE,
} from './constants';
import { formReducer, init, isEmptyOrAllFalse } from './utils';
import * as Styled from './styles';
import {
  getAssesmentsForms,
  getEducationForms,
  getEnforcementAgencies,
  getEnforcementForms,
} from '_entities/Report/Report.actions';
import NEW_REPORTS_TYPES from '_constants/NEW_REPORTS_TYPES';
import {
  getReportAssessmentsForms,
  getReportEducationForms,
  getReportEnforcementAgencies,
  getReportEnforcementForms,
} from '_entities/Report/Report.selectors';
import { isEmpty } from 'lodash';
import ExportDataDropDown from 'ui-kit/old/ExportDataDropDown';
const { getCalcPoly } = ReportEntity.actions;
const { getReportsType, getReportsAreas } = ReportEntity.selectors;
const formatDate = (date) => moment(date).format('YYYY-MM-DD');

const FORMS_OBJECT = {
  [NEW_REPORTS_TYPES.ASSESMENT]: getReportAssessmentsForms,
  [NEW_REPORTS_TYPES.EDUCATION]: getReportEducationForms,
  [NEW_REPORTS_TYPES.ENFORCEMENT]: getReportEnforcementForms,
  [NEW_REPORTS_TYPES.LANDSCAPE]: () => {},
};

function CustomReportForm({ title, text, apiCall, type }) {
  const [state, formDispatch] = useReducer(formReducer, formInitialState, init);
  const [isLoading, setIsLoading] = useState(true);
  const reportsType = useSelector(getReportsType);
  const reportsArea = useSelector(getReportsAreas);
  const formOptions = useSelector(FORMS_OBJECT?.[type]);
  const agenciesOptions = useSelector(getReportEnforcementAgencies);
  const dispatch = useDispatch();
  const history = useHistory();
  const { data: formStateData, errors: formStateErrors, canBeSubmitted } = state;

  const {
    interestType,
    interestArea,
    comparisonType,
    comparisonArea,
    dateStart,
    dateEnd,
    reportType,
    statewide,
    sellsTobacoo,
    formIds,
    agencyIds,
  } = formNames;
  const getAreaOptions = useCallback((type) => reportsArea[type?.value], [reportsArea]);

  const isAEEVisitBasedReport = useMemo(() => {
    return [NEW_REPORTS_TYPES.ASSESMENT, NEW_REPORTS_TYPES.EDUCATION, NEW_REPORTS_TYPES.ENFORCEMENT].includes(type);
  }, [type]);

  const isState = useMemo(() => {
    return formStateData?.[interestType]?.slug?.includes('state');
  }, [formStateData?.[interestType]?.slug]);

  useEffect(() => {
    (async () => {
      try {
        if (
          formStateData[dateStart] &&
          formStateData[dateEnd] &&
          !moment(formStateData[dateStart]).isAfter(moment(formStateData[dateEnd]))
        ) {
          formDispatch({
            type: FORM_ACTION_TYPES.CHANGE_DATE,
            shortForm: formStateData[reportType] === REPORT_GEO_TYPES.oneGeography,
            requiredFields: NEW_REPORTS_REQUIRED_FIELDS[type],
          });
          const params = `start_date=${formatDate(formStateData[dateStart])}&end_date=${formatDate(
            formStateData[dateEnd],
          )}`;
          setIsLoading(true);
          switch (type) {
            case NEW_REPORTS_TYPES.ASSESMENT:
              await dispatch(getAssesmentsForms(params));
              break;
            case NEW_REPORTS_TYPES.EDUCATION:
              await dispatch(getEducationForms(params));
              break;
            case NEW_REPORTS_TYPES.ENFORCEMENT:
              await dispatch(getEnforcementForms(params));
            default:
              break;
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [formStateData[dateStart], formStateData[dateEnd]]);

  useEffect(() => {
    (async () => {
      try {
        await dispatch(getCalcPoly());
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    })();
    return () => {
      formDispatch({ type: FORM_ACTION_TYPES.RESET_DATA });
    };
  }, []);

  const handleSelectChange = useCallback(
    (obj, name) => {
      formDispatch({
        type: FORM_ACTION_TYPES.ENTER_DATA,
        name,
        payload: obj,
        shortForm: formStateData[reportType] === REPORT_GEO_TYPES.oneGeography,
        requiredFields: NEW_REPORTS_REQUIRED_FIELDS[type],
      });
      if (name === interestType) {
        formDispatch({
          type: FORM_ACTION_TYPES.ENTER_DATA,
          name: interestArea,
          payload: null,
          shortForm: formStateData[reportType] === REPORT_GEO_TYPES.oneGeography,
          requiredFields: NEW_REPORTS_REQUIRED_FIELDS[type],
        });
      }
      if (name === comparisonType) {
        formDispatch({
          type: FORM_ACTION_TYPES.ENTER_DATA,
          name: comparisonArea,
          payload: null,
          shortForm: formStateData[reportType] === REPORT_GEO_TYPES.oneGeography,
          requiredFields: NEW_REPORTS_REQUIRED_FIELDS[type],
        });
      }
    },
    [formDispatch, formStateData[reportType]],
  );

  const handlePickerChange = useCallback(
    (date, name) => {
      formDispatch({
        type: FORM_ACTION_TYPES.ENTER_DATA,
        name,
        payload: date,
        shortForm: formStateData[reportType] === REPORT_GEO_TYPES.oneGeography,
        requiredFields: NEW_REPORTS_REQUIRED_FIELDS[type],
      });
    },
    [formDispatch, formStateData[reportType]],
  );

  const handleInvalidDate = useCallback(
    (errorMessage, fieldName) => {
      formDispatch({
        type: FORM_ACTION_TYPES.SET_INVALID_DATE_ERROR,
        name: fieldName,
        payload: errorMessage,
      });
    },
    [formDispatch],
  );

  const handleSubmit = (e) => {
    formDispatch({
      type: FORM_ACTION_TYPES.VALIDATE_DATA,
      shortForm: reportType === REPORT_GEO_TYPES.oneGeography,
      requiredFields: NEW_REPORTS_REQUIRED_FIELDS[type],
      shortForm: formStateData[reportType] === REPORT_GEO_TYPES.oneGeography,
    });

    setIsLoading(true);
    (async () => {
      const {
        interest_area: newInterestArea,
        comparison_area: newComparisonArea,
        date_start: newDateStart,
        date_end: newDateEnd,
        statewide,
        report_type: reportType,
        sells_tobacco: sellsTobacoo,
        form_ids: formIds,
        agency_ids: agencyIds,
      } = formStateData;
      if (!canBeSubmitted) {
        setIsLoading(false);
        return;
      }

      const formatDate = (date) => moment(date).format('YYYY-MM-DD');
      const payload = {
        delivery_method: DOWNLOAD_TYPE[e.label].type,
        ...(isAEEVisitBasedReport && { visit_type: type }),
        ...(newInterestArea?.value && { calcpoly1: newInterestArea.value }),
        ...(newComparisonArea?.value &&
          reportType === REPORT_GEO_TYPES.twoGeography && { calcpoly2: newComparisonArea.value }),
        ...(newDateStart && { start_date: formatDate(newDateStart) }),
        ...(newDateEnd && { end_date: formatDate(newDateEnd) }),
        // ...(sellsTobacoo && { sells_tobacco: sellsTobacoo }),
        ...(!isEmpty(formIds) && isAEEVisitBasedReport && { form_ids: formIds.map(({ key }) => key) }),
        ...(statewide?.slug &&
          reportType === REPORT_GEO_TYPES.oneGeography &&
          isState && { statewide_slug: statewide.slug }),
      };

      try {
        const res = await dispatch(apiCall(payload));
        if (res?.data?.task_id) {
          await formDispatch({ type: FORM_ACTION_TYPES.RESET_DATA });
          localStorage.setItem('lastReportTime', Date.now().toString());
          const existingReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
          const uniqueReports = [...new Set([...existingReports, res.data.task_id])];
          localStorage.setItem('generatedReports', JSON.stringify(uniqueReports));
          notification.success(DOWNLOAD_TYPE[e.label].message);
          history.push(URLS.reports);
        }
      } catch (err) {
        console.log(err);
        notification.error(MESSAGES.COMMON_ERROR);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <Styled.MainWrapper>
      {isLoading && <Loader />}
      <BackPageButton />
      <Styled.Title>{title}</Styled.Title>
      <Styled.Description>{text}</Styled.Description>
      <Styled.SubTitle>* - required fields</Styled.SubTitle>
      <Styled.Form>
        <Styled.RadioWrapper>
          <RadioButtonGroup
            name="reportType"
            title="Select report type*"
            options={TOBACCO_LANDSCAPE_OPTIONS}
            direction="row"
            value={formStateData[reportType]}
            onChange={({ target: { value } }) => {
              handleSelectChange(value, reportType);
            }}
          />
        </Styled.RadioWrapper>
        <Styled.FormRow>
          <BaseSelect
            name={interestType}
            labelText="Select a geography type"
            options={reportsType}
            value={formStateData[interestType]}
            error={formStateErrors[interestType]}
            onChange={(options) => handleSelectChange(options, interestType)}
            placeholder="Select an option"
            inputId={interestType}
            required
          />
          <BaseSelect
            name={interestArea}
            labelText="Select a geography"
            options={getAreaOptions(formStateData[interestType])}
            value={formStateData[interestArea]}
            error={formStateErrors[interestArea]}
            onChange={(options) => handleSelectChange(options, interestArea)}
            placeholder="Select an option"
            inputId={interestArea}
            required
          />
          {formStateData[reportType] === REPORT_GEO_TYPES.oneGeography && isState && (
            <BaseSelect
              name={statewide}
              labelText="Select statewide option"
              options={reportsType.slice(
                reportsType.findIndex(({ value }) => value === formStateData[interestType]?.value) + 1,
              )}
              value={formStateData[statewide]}
              error={formStateErrors[statewide]}
              onChange={(options) => handleSelectChange(options, statewide)}
              placeholder="Select an option"
              inputId={statewide}
              isClearable
            />
          )}
        </Styled.FormRow>
        {formStateData[reportType] === REPORT_GEO_TYPES.twoGeography && (
          <Styled.FormRow>
            <BaseSelect
              name={comparisonType}
              labelText="Select a comparison type"
              options={reportsType}
              value={formStateData[comparisonType]}
              error={formStateErrors[comparisonType]}
              onChange={(options) => handleSelectChange(options, comparisonType)}
              placeholder="Select an option"
              inputId={comparisonType}
              required
            />
            <BaseSelect
              name={comparisonArea}
              labelText="Select a comparison area"
              options={getAreaOptions(formStateData[comparisonType])}
              value={formStateData[comparisonArea]}
              error={formStateErrors[comparisonArea]}
              onChange={(options) => handleSelectChange(options, comparisonArea)}
              placeholder="Select an option"
              inputId={comparisonArea}
              required
            />
          </Styled.FormRow>
        )}
        {isAEEVisitBasedReport && (
          <Styled.FormRow isColumn>
            <Styled.Label>Enter reporting period *</Styled.Label>
            <Styled.PickerWrapper>
              <Picker
                label="Start date (mm/dd/yyyy)*"
                showMonthYearDropdown
                idInput={dateStart}
                onChange={(date) => handlePickerChange(date, dateStart)}
                onInvalidDate={(error) => handleInvalidDate(error, dateStart)}
                selected={formStateData[dateStart]}
                error={formStateErrors[dateStart]}
                inputFor={dateStart}
                isFullWidth
                name={dateStart}
                required
                hasFutureValidation
              />
              <Picker
                label="End date (mm/dd/yyyy)*"
                showMonthYearDropdown
                idInput={dateEnd}
                onChange={(date) => handlePickerChange(date, dateEnd)}
                onInvalidDate={(error) => handleInvalidDate(error, dateEnd)}
                selected={formStateData[dateEnd] || null}
                error={formStateErrors[dateEnd]}
                inputFor={dateEnd}
                isFullWidth
                name={dateEnd}
                required
                hasFutureValidation
              />
            </Styled.PickerWrapper>
          </Styled.FormRow>
        )}
        {isAEEVisitBasedReport && formStateData[dateStart] && formStateData[dateEnd] && (
          <Styled.FormRow>
            <BaseSelect
              value={formStateData[formIds]}
              error={formStateErrors[formIds]}
              labelText="Select survey form(s)"
              onChange={(e) => handleSelectChange(e, formIds)}
              inputId="surveyReportDrop"
              options={formOptions}
              menuPlacement="bottom"
              isMulti
              allowSelectAll={!isEmpty(formOptions)}
              required
            />
          </Styled.FormRow>
        )}
        {/* {type === NEW_REPORTS_TYPES.ENFORCEMENT && formStateData[dateStart] && formStateData[dateEnd] && (
          <Styled.FormRow>
            <BaseSelect
              value={formStateData[agencyIds]}
              error={formStateErrors[agencyIds]}
              labelText="Select agency(ies)"
              onChange={(e) => handleSelectChange(e, agencyIds)}
              inputId="agencyReportDrop"
              options={agenciesOptions}
              menuPlacement="bottom"
              isMulti
              allowSelectAll={!isEmpty(agenciesOptions)}
              required
            />
          </Styled.FormRow>
        )} */}
        {/* {(isAEEVisitBasedReport  || NEW_REPORTS_TYPES.ENFORCEMENT === type) && (
          <Styled.FormRow isColumn>
            <Styled.Label>Select retailer criteria</Styled.Label>
            <Checkbox
              value={formStateData[formNames.sellsTobacoo]}
              onChange={({ target: { value } }) => handleSelectChange(value, sellsTobacoo)}
              label="Sells Tobacco"
              name="Sells Tobacco"
            />
          </Styled.FormRow>
        )} */}
        <Styled.ExportWrapper>
          <ExportDataDropDown
            onChange={(e) => handleSubmit(e)}
            disabled={!isEmptyOrAllFalse(formStateErrors)}
            value={null}
            options={EXPORT_DATA_OPTIONS}
            placeholder={'Download/Email'}
            isDisabled={isLoading}
          />
        </Styled.ExportWrapper>
      </Styled.Form>
    </Styled.MainWrapper>
  );
}

CustomReportForm.propTypes = {
  title: PropTypes.string.isRequired,
  apiCall: PropTypes.func.isRequired,
  text: PropTypes.string,
  type: PropTypes.number,
};

CustomReportForm.defaultProps = {
  text: '',
};

export default CustomReportForm;
